/**
 * HTTP header construction for the auth package.
 *
 * Reads the JWT from the `user` cookie and attaches it as a `Bearer` token.
 * A simple cache avoids re-parsing the cookie string when the raw value
 * hasn't changed between calls.
 *
 * @module
 */
import Cookies from 'js-cookie';

let cachedJwt: string | undefined;
let lastRawCookie: string | undefined;

/**
 * Extracts the JWT from the `user` cookie, caching the result until the
 * raw cookie string changes.  Returns `undefined` when the cookie is
 * absent or unparseable.
 */
function readJwtFromCookie(): string | undefined {
  try {
    const raw = Cookies.get('user');
    if (raw === lastRawCookie) return cachedJwt;
    lastRawCookie = raw;
    if (!raw) { cachedJwt = undefined; return cachedJwt; }
    const user = JSON.parse(raw) as { jwt?: string } | null;
    cachedJwt = user?.jwt;
    return cachedJwt;
  } catch (err) {
    console.warn('[auth] cookie parse failure, proceeding without auth header', err);
    lastRawCookie = undefined;
    cachedJwt = undefined;
    return undefined;
  }
}

/**
 * Builds the default header set for client-app (Rails) API requests.
 *
 * Includes `Content-Type`, Zoocasa request-source / generation markers,
 * the optional API key, and — when present — the JWT `Authorization` header.
 */
export function getDefaultHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Zoocasa-Request-Source': 'zoocasa.com',
    'X-Zoocasa-Generation': 'next',
  };
  const apiKey = typeof process !== 'undefined' ? process.env.NEXT_PUBLIC_ZOOCASA_API_KEY : undefined;
  if (apiKey) {
    headers['x-zoocasa-api-key'] = apiKey;
  }
  const jwt = readJwtFromCookie();
  if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  }
  return headers;
}

/**
 * Builds the default header set for Cognito auth-API requests.
 *
 * Same as {@link getDefaultHeaders} but strips the Zoocasa-specific
 * request-source and generation markers that the auth API does not expect.
 */
export function getCognitoDefaultHeaders(): Record<string, string> {
  const headers = getDefaultHeaders();
  delete headers['X-Zoocasa-Request-Source'];
  delete headers['X-Zoocasa-Generation'];
  return headers;
}

/**
 * Clears the memoised JWT cache.
 * **Test use only** — never call this in production code.
 */
export function _resetHeaderCacheForTests(): void {
  cachedJwt = undefined;
  lastRawCookie = undefined;
}
